import { useEffect, useRef } from 'react';
import type { RemoteTrack, RemoteTrackPublication } from 'livekit-client';
import { RemoteParticipant, RoomEvent, Track } from 'livekit-client';
import { selectRoom } from 'state/selectors';
import { useUserSettingsContext } from 'contexts';

// =====================================================================================
// Управление громкостью спикера (выходного аудио от удалённых участников звонка).
//
// Браузер не даёт ни читать, ни менять системную громкость колонок,
// поэтому мы регулируем громкость только внутри своего приложения, на уровне <audio>.volume.
// =====================================================================================

// Укладываем значение в диапазон [0, 1] ( такой принимает HTMLMediaElement.volume)
// и, соответственно, RemoteParticipant.setVolume.
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/**
 * Применяет громкость к одному участнику.
 */
const applyVolume = (participant: RemoteParticipant, volume: number): void => {
    participant.setVolume(volume);
};

export const useApplySpeakerVolume = (): void => {
    const room = selectRoom();
    const { speakerVolume } = useUserSettingsContext();

    // хранит всегда актуальное значение громкости, что избавляет от применения старого значения из замыкания для участников мита
    const volumeRef = useRef(clamp01(speakerVolume));
    volumeRef.current = clamp01(speakerVolume);

    useEffect(
        function redefinedRemoteParticipantsVolume() {
            if (!room) return;
            const v = clamp01(speakerVolume);
            room.remoteParticipants.forEach((p) => applyVolume(p, v));
        },
        [room, speakerVolume],
    );

    useEffect(
        function subscribeOnNewParticipantAndNewAudioTrack() {
            if (!room) return;

            // Подписка на нового удалённогоь участника
            const onParticipantConnected = (p: RemoteParticipant) => {
                applyVolume(p, volumeRef.current);
            };

            // Подписка на новый аудио-трек
            const onTrackSubscribed = (
                _track: RemoteTrack,
                publication: RemoteTrackPublication,
                participant: RemoteParticipant,
            ) => {
                if (publication.kind !== Track.Kind.Audio) return;
                applyVolume(participant, volumeRef.current);
            };

            room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
            room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);

            return () => {
                room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
                room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
            };
        },
        [room],
    );
};
